import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import EmployeesPage from "@/pages/Employees";
import FleetPage from "@/pages/Fleet";
import VehicleDetail from "@/pages/VehicleDetail";
import FinancePage from "@/pages/Finance";
import AccountingPage from "@/pages/Accounting";
import NewContract from "@/pages/NewContract";
import ContractHistory from "@/pages/ContractHistory";
import ContractView from "@/pages/ContractView";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import UsersPage from "@/pages/Users";
import AdminPage from "@/pages/Admin";
import RoutesPage from "@/pages/Routes";
import Schedule from "@/pages/Schedule";
import Welcome from "@/pages/Welcome";
import Register from "@/pages/Register";
import KierowcaHome from "@/pages/KierowcaHome";
import { DeliveryDetail, DeliveryStopDetail } from "@/pages/Deliveries";
import AchievementsPage from "@/pages/Achievements";
import ShopPage from "@/pages/Shop";
import GamificationAdminPage from "@/pages/GamificationAdmin";
import DriverRatesPage from "@/pages/DriverRates";
import Zlecenia from "@/pages/Zlecenia";
import LoadingPage from "@/pages/Loading";
import CourierMapPage from "@/pages/CourierMap";
import HRPage from "@/pages/HR";
import { LocationTracker } from "@/components/LocationTracker";
import AdminConsents from "@/pages/AdminConsents";
import AdminBroadcasts from "@/pages/AdminBroadcasts";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ConfirmProvider } from "@/components/ConfirmProvider";
import { hasPermission } from "@/lib/permissions";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  // Process OAuth callback FIRST, synchronously during render — prevents race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      {/* Driver-only home — kierowca lands here straight after login. */}
      <Route
        path="/kierowca"
        element={
          <ProtectedRoute allowDriver>
            <KierowcaHome />
          </ProtectedRoute>
        }
      />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardOrRedirect />} />
        <Route path="/employees" element={<PermGate perm="employees"><EmployeesPage /></PermGate>} />
        <Route path="/fleet" element={<PermGate perm="fleet"><FleetPage /></PermGate>} />
        <Route path="/fleet/:id" element={<PermGate perm="fleet"><VehicleDetail /></PermGate>} />
        <Route path="/finance" element={<PermGate perm="finance"><FinancePage /></PermGate>} />
        <Route path="/accounting" element={<PermGate perm="finance"><AccountingPage /></PermGate>} />
        <Route path="/routes" element={<PermGate perm="routes"><RoutesPage /></PermGate>} />
        <Route path="/zlecenia" element={<PermGate perm="deliveries"><Zlecenia /></PermGate>} />
        <Route path="/deliveries" element={<Navigate to="/zlecenia?view=map" replace />} />
        <Route path="/deliveries/:id" element={<PermGate perm="deliveries"><DeliveryDetail /></PermGate>} />
        <Route path="/deliveries/:routeId/stop/:stopId" element={<PermGate perm="deliveries"><DeliveryStopDetail /></PermGate>} />
        <Route path="/moja-trasa" element={<Navigate to="/zlecenia?view=swipe" replace />} />
        <Route path="/praca" element={<Navigate to="/zlecenia?view=list" replace />} />
        <Route path="/zaladunek" element={<LoadingPage />} />
        <Route path="/mapa" element={<PermGate perm="routes"><CourierMapPage /></PermGate>} />
        <Route path="/kadry" element={<PermGate perm="hr"><HRPage /></PermGate>} />
        <Route path="/osiagniecia" element={<AchievementsPage />} />
        <Route path="/sklep" element={<ShopPage />} />
        <Route
          path="/admin/gamifikacja"
          element={
            <ProtectedRoute adminOnly>
              <GamificationAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/stawki"
          element={
            <ProtectedRoute adminOnly>
              <DriverRatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/zgody"
          element={
            <ProtectedRoute adminOnly>
              <AdminConsents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/komunikaty"
          element={
            <ProtectedRoute adminOnly>
              <AdminBroadcasts />
            </ProtectedRoute>
          }
        />
        <Route path="/schedule" element={<PermGate perm="schedule"><Schedule /></PermGate>} />
        <Route path="/contracts/new" element={<PermGate perm="contracts"><NewContract /></PermGate>} />
        <Route path="/contracts" element={<PermGate perm="contracts"><ContractHistory /></PermGate>} />
        <Route path="/contracts/:id" element={<PermGate perm="contracts"><ContractView /></PermGate>} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={<Navigate to="/admin" replace />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

// ---- helpers ---------------------------------------------------------------

function PermGate({ perm, children }) {
  const { user } = useAuth();
  if (!hasPermission(user, perm)) return <Navigate to="/" replace />;
  return children;
}

function DashboardOrRedirect() {
  const { user } = useAuth();
  if (!hasPermission(user, "dashboard")) {
    // Find the first permission the user has and redirect to that section.
    const fallbackMap = {
      finance: "/finance",
      fleet: "/fleet",
      routes: "/routes",
      deliveries: "/zlecenia",
      contracts: "/contracts",
      employees: "/employees",
      admin: "/admin",
    };
    for (const p of user?.permissions || []) {
      if (fallbackMap[p]) return <Navigate to={fallbackMap[p]} replace />;
    }
    // No permissions at all (defensive) — shouldn't happen because isDriverOnly catches earlier.
    return <Navigate to="/kierowca" replace />;
  }
  return <Dashboard />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LocationTracker />
          <ConfirmProvider>
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  borderRadius: 0,
                  border: "1px solid #18181B",
                  background: "#fff",
                  color: "#09090B",
                },
              }}
            />
            <AppRouter />
          </ConfirmProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
